import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Receipt, DownloadSimple, PaperPlaneTilt, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TypePill = ({ type }) => {
  const isDeposit = type === 'deposit';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
      isDeposit 
        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' 
        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    }`}>
      {isDeposit ? 'Acompte' : 'Finale'}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const config = {
    draft: { label: 'Brouillon', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-300' },
    sent: { label: 'Envoyée', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
    paid: { label: 'Payée', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
  };
  const c = config[status] || config.draft;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{c.label}</span>;
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur téléchargement');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`);
      toast.success('Facture envoyée');
      fetchInvoices();
    } catch (error) {
      toast.error('Erreur envoi');
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/mark-paid`);
      toast.success('Marquée payée');
      fetchInvoices();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filters = [
    { key: 'all', label: 'Toutes' },
    { key: 'draft', label: 'Brouillons' },
    { key: 'sent', label: 'Envoyées' },
    { key: 'paid', label: 'Payées' },
  ];

  const filteredInvoices = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <Receipt size={20} className="text-emerald-600" weight="duotone" />
        </div>
        <span className="text-lg font-semibold text-foreground">Factures</span>
        <span className="text-sm text-muted-foreground">({invoices.length})</span>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              filter === f.key
                ? 'bg-black dark:bg-white text-white dark:text-black'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      <div className="space-y-3">
        {filteredInvoices.length > 0 ? (
          filteredInvoices.map((invoice, index) => (
            <motion.div
              key={invoice.invoice_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="premium-card p-4 sm:p-5"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-semibold text-foreground">{invoice.invoice_number}</span>
                    <TypePill type={invoice.invoice_type} />
                    <StatusPill status={invoice.status} />
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{invoice.client_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-foreground">{invoice.amount_ttc?.toFixed(0)} €</p>
                  <p className="text-xs text-muted-foreground">TTC</p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => handleDownloadPDF(invoice.invoice_id, invoice.invoice_number)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors"
                >
                  <DownloadSimple size={18} />
                  PDF
                </button>
                
                {invoice.status === 'draft' && (
                  <button
                    onClick={() => handleSendInvoice(invoice.invoice_id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm font-medium text-white transition-colors"
                  >
                    <PaperPlaneTilt size={18} />
                    Envoyer
                  </button>
                )}
                
                {invoice.status === 'sent' && (
                  <button
                    onClick={() => handleMarkAsPaid(invoice.invoice_id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-sm font-medium text-white transition-colors"
                  >
                    <CheckCircle size={18} />
                    Payée
                  </button>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="premium-card p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Receipt size={32} className="text-muted-foreground" weight="duotone" />
            </div>
            <p className="text-muted-foreground">Aucune facture</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
